import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Leaf,
  ArrowRight,
  Factory,
  Building2,
  GraduationCap,
  ShieldCheck,
  Layers,
  FileSpreadsheet,
  Scale,
  FileCheck2,
  Users,
  Calculator,
  Globe2,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";
import { useI18n } from "./i18n";
import LangToggle from "./components/LangToggle";
import StandardsLogos from "./components/StandardsLogos";

/**
 * WelcomeSegment — light-themed, bilingual entry page.
 * Contains segment selector + Features / Process / Use cases / Contact sections
 * as in-page anchors so navigation stays on the light experience.
 * The dark marketing page remains available at /landing.
 */
export default function WelcomeSegment() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const isEn = lang === "en";

  const segments = [
    {
      id: "sme",
      role: "organization",
      icon: Factory,
      title_tr: "İhracatçı KOBİ",
      title_en: "Exporting SMB",
      desc_tr: "Tek ya da az sayıda tesis · CBAM'e tabi ihracat · Excel'den kurtulmak istiyorum.",
      desc_en: "Single or few facilities · CBAM-liable exporter · ready to leave Excel behind.",
      bullet_tr: "Kapsam 1-2-3, CBAM XML, PDF rapor",
      bullet_en: "Scope 1-2-3, CBAM XML, PDF report",
      accent: "#14512f",
      trial: true,
      cta_tr: "Denemeyi başlat",
      cta_en: "Start free trial",
    },
    {
      id: "enterprise",
      role: "organization",
      icon: Building2,
      title_tr: "Kurumsal şirket",
      title_en: "Enterprise / Group",
      desc_tr: "Çok iştirakli grup · CSRD/ESRS raporlaması · SSO ve dedicated altyapı.",
      desc_en: "Multi-entity group · CSRD/ESRS reporting · SSO & dedicated infrastructure.",
      bullet_tr: "IFRS S1/S2, ESRS E1, SSO, audit log",
      bullet_en: "IFRS S1/S2, ESRS E1, SSO, audit log",
      accent: "#0f3d24",
      trial: true,
      cta_tr: "Denemeyi başlat",
      cta_en: "Start free trial",
    },
    {
      id: "consultant",
      role: "consultant",
      icon: GraduationCap,
      title_tr: "Sürdürülebilirlik Danışmanı",
      title_en: "Sustainability Consultant",
      desc_tr: "Birden çok müşteri portföyü · her müşteri için ayrı white-label rapor.",
      desc_en: "Multiple client portfolio · per-client white-label reports.",
      bullet_tr: "Sınırsız müşteri workspace, multi-tenant",
      bullet_en: "Unlimited client workspace, multi-tenant",
      accent: "#2f8f5b",
      demoOnly: true,
      cta_tr: "Örnek rapor iste",
      cta_en: "Request sample report",
    },
  ];

  const features = [
    {
      icon: Layers,
      title_tr: "Kapsam 1 · 2 · 3 Hesaplama",
      title_en: "Scope 1 · 2 · 3 Calculation",
      text_tr: "Doğrudan, enerji dolaylı ve değer zinciri emisyonlarını GHG Protokolü metodolojisiyle uçtan uca hesapla.",
      text_en: "Calculate direct, energy indirect and value chain emissions end-to-end with the GHG Protocol methodology.",
    },
    {
      icon: FileSpreadsheet,
      title_tr: "CBAM · PDF & XML Export",
      title_en: "CBAM · PDF & XML Export",
      text_tr: "Çeyreklik Sınırda Karbon Düzenleme Mekanizması yükümlülüğünü hesapla ve resmi formatta dışa aktar.",
      text_en: "Compute your quarterly CBAM obligation and export in the official PDF & XML formats.",
    },
    {
      icon: ShieldCheck,
      title_tr: "ESRS + GRI + IFRS S1/S2",
      title_en: "ESRS + GRI + IFRS S1/S2",
      text_tr: "ESRS E1–E5, S1–S4, G1, GRI ve IFRS S1/S2 açıklamalarını tek şablonda birleştir.",
      text_en: "Unify ESRS E1–E5, S1–S4, G1, GRI and IFRS S1/S2 disclosures in one template.",
    },
    {
      icon: Scale,
      title_tr: "Global Karbon Vergisi Kıyası",
      title_en: "Global Carbon Tax Comparison",
      text_tr: "Ülke bazlı karbon fiyatlandırmalarını karşılaştır, maruziyetini öngör.",
      text_en: "Compare country-level carbon pricing and forecast your exposure.",
    },
    {
      icon: FileCheck2,
      title_tr: "Denetim İzi (Audit Trail)",
      title_en: "Audit Trail",
      text_tr: "ISO 14064-3 / ISAE 3000 doğrulayıcıları için değişmez kayıt.",
      text_en: "Immutable record for ISO 14064-3 / ISAE 3000 verifiers.",
    },
    {
      icon: Users,
      title_tr: "Danışman Portalı",
      title_en: "Consultant Portal",
      text_tr: "Birden çok müşterinin karbon hesabını tek panelden yönet.",
      text_en: "Manage multiple clients' carbon accounts from a single console.",
    },
  ];

  const process = [
    {
      no: "01",
      title_tr: "Hesapla",
      title_en: "Calculate",
      text_tr: "Faaliyet verilerini içe aktar; Kapsam 1, 2 ve 3 emisyonlarını GHG Protokolü faktörleriyle otomatik hesapla.",
      text_en: "Import activity data; automatically calculate Scope 1, 2 and 3 emissions with GHG Protocol factors.",
    },
    {
      no: "02",
      title_tr: "Raporla",
      title_en: "Report",
      text_tr: "ESRS E1–E5, S1–S4, G1, GRI ve IFRS S1/S2 açıklamalarını üret; CBAM yükümlülüğünü PDF ve XML olarak dışa aktar.",
      text_en: "Produce ESRS E1–E5, S1–S4, G1, GRI and IFRS S1/S2 disclosures; export CBAM obligation as PDF and XML.",
    },
    {
      no: "03",
      title_tr: "Doğrula",
      title_en: "Verify",
      text_tr: "ISO 14064-3 / ISAE 3000 doğrulayıcıları için tam denetim iziyle bağımsız güvence sürecini hızlandır.",
      text_en: "Accelerate independent assurance for ISO 14064-3 / ISAE 3000 verifiers with a full audit trail.",
    },
  ];

  const useCases = [
    { icon: Factory, title_tr: "Büyük Kuruluşlar", title_en: "Large Enterprises", text_tr: "Grup düzeyinde konsolide emisyon ve CSRD uyumu.", text_en: "Group-level consolidated emissions and CSRD compliance." },
    { icon: Users, title_tr: "KOBİ'ler", title_en: "SMBs", text_tr: "Tedarik zinciri talepleri için hızlı ISO 14064 raporu.", text_en: "Fast ISO 14064 report for supply-chain requests." },
    { icon: Calculator, title_tr: "Danışmanlar", title_en: "Consultants", text_tr: "Portföy genelinde ölçeklenebilir raporlama.", text_en: "Scalable reporting across a client portfolio." },
    { icon: Globe2, title_tr: "Doğrulayıcılar", title_en: "Verifiers", text_tr: "ISAE 3000 uyumlu, denetlenebilir veri seti.", text_en: "ISAE 3000-ready, auditable dataset." },
  ];

  const pick = (s) => {
    if (s.demoOnly) {
      nav(`/pricing?plan=${s.id}`);
    } else {
      nav(`/register?role=${s.role}&segment=${s.id}`);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#FAFAF5] text-[#1b2420]">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(1000px 500px at 15% -10%, rgba(20,81,47,0.06), transparent 60%), radial-gradient(900px 500px at 100% 100%, rgba(212,248,112,0.14), transparent 55%)",
        }}
      />

      <div className="absolute right-4 top-4 z-30 sm:right-6 sm:top-6">
        <LangToggle />
      </div>

      <div className="relative z-10 mx-auto max-w-5xl px-6 py-14 lg:py-20">
        {/* Top nav */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Link to="/" className="inline-flex items-center gap-2.5" data-testid="welcome-brand-link">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#14512f] text-[#D4F870] shadow-sm">
              <Leaf className="h-5 w-5" strokeWidth={2.5} />
            </span>
            <span className="font-display text-lg tracking-tight text-[#14512f] sm:text-xl">
              Enasuspro<sup className="text-[10px] text-[#6b7268]">®</sup> Carbon Ledger
            </span>
          </Link>
          <nav className="hidden items-center gap-6 text-[13px] text-[#4b544c] md:flex" data-testid="welcome-topnav">
            <a href="#features" className="hover:text-[#14512f] transition-colors" data-testid="welcome-nav-features">
              {isEn ? "Features" : "Özellikler"}
            </a>
            <a href="#process" className="hover:text-[#14512f] transition-colors" data-testid="welcome-nav-process">
              {isEn ? "Process" : "Süreç"}
            </a>
            <a href="#use-cases" className="hover:text-[#14512f] transition-colors" data-testid="welcome-nav-usecases">
              {isEn ? "Use cases" : "Kullanım"}
            </a>
            <Link to="/pricing" className="hover:text-[#14512f] transition-colors" data-testid="welcome-nav-pricing">
              {isEn ? "Pricing" : "Fiyatlar"}
            </Link>
            <Link to="/docs" className="hover:text-[#14512f] transition-colors" data-testid="welcome-nav-docs">
              {isEn ? "Docs" : "Dokümanlar"}
            </Link>
            <a href="#contact" className="hover:text-[#14512f] transition-colors" data-testid="welcome-nav-contact">
              {isEn ? "Contact" : "İletişim"}
            </a>
          </nav>
          <Link to="/login" className="text-[13px] font-medium text-[#14512f] hover:underline" data-testid="welcome-login-link">
            {isEn ? "Sign in →" : "Giriş yap →"}
          </Link>
        </div>

        {/* Heading */}
        <div className="mt-10 max-w-2xl">
          <p
            className="inline-flex items-center gap-2 rounded-full border border-[#14512f]/15 bg-white/70 px-3 py-1 text-[11px] font-medium uppercase tracking-widest text-[#14512f]"
            data-testid="welcome-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#14512f]" />
            {isEn ? "Let's tailor your setup" : "Kuruluma birlikte başlayalım"}
          </p>
          <h1
            className="mt-4 font-display text-4xl leading-[1.05] tracking-tight text-[#16321f] sm:text-5xl"
            data-testid="welcome-title"
          >
            {isEn ? "Which best describes you?" : "Sizi en iyi hangisi tanımlıyor?"}
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-[#5c6259]" data-testid="welcome-subtitle">
            {isEn
              ? "One click and we prepare the right onboarding, plan suggestion and factor set for you."
              : "Bir tıkla size özel onboarding, plan önerisi ve emisyon faktörü setini hazırlıyoruz."}
          </p>
        </div>

        {/* Segment cards */}
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {segments.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => pick(s)}
                data-testid={`segment-${s.id}`}
                className="group relative flex flex-col items-start rounded-2xl border border-[#e4e6dd] bg-white p-6 text-left transition-all hover:-translate-y-0.5 hover:border-[#14512f]/40 hover:shadow-lg"
                style={{ boxShadow: "0 4px 14px -8px rgba(20,81,47,0.10)" }}
              >
                {s.trial && (
                  <span
                    data-testid={`segment-trial-badge-${s.id}`}
                    className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-[#D4F870] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-[#14512f]"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-[#14512f]" />
                    {isEn ? "3-day free trial" : "3 gün ücretsiz deneme"}
                  </span>
                )}
                <span
                  className="grid h-11 w-11 place-items-center rounded-xl transition-colors"
                  style={{ backgroundColor: `${s.accent}14` }}
                >
                  <Icon className="h-5 w-5" style={{ color: s.accent }} strokeWidth={2} />
                </span>
                <h3 className="mt-4 font-display text-xl leading-tight text-[#16321f]">
                  {isEn ? s.title_en : s.title_tr}
                </h3>
                <p className="mt-2 text-[14px] leading-relaxed text-[#5c6259]">
                  {isEn ? s.desc_en : s.desc_tr}
                </p>
                <p className="mt-3 text-[12px] font-medium uppercase tracking-widest" style={{ color: s.accent }}>
                  {isEn ? s.bullet_en : s.bullet_tr}
                </p>
                <div className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#14512f] transition-transform group-hover:translate-x-0.5">
                  {isEn ? s.cta_en : s.cta_tr} <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} />
                </div>
              </button>
            );
          })}
        </div>

        {/* Bottom links */}
        <div className="mt-10 flex flex-col items-center gap-4 border-t border-[#eef0e7] pt-8 sm:flex-row sm:justify-between">
          <div className="text-[13px] text-[#6b7268]">
            {isEn ? "Not sure yet?" : "Emin değil misiniz?"}{" "}
            <Link to="/pricing" className="font-medium text-[#14512f] hover:underline" data-testid="welcome-pricing-link">
              {isEn ? "See plans & pricing" : "Planları ve fiyatları görün"}
            </Link>
          </div>
          <div className="text-[13px] text-[#6b7268]">
            {isEn ? "Already have an account?" : "Hesabınız var mı?"}{" "}
            <Link to="/login" className="font-medium text-[#14512f] hover:underline" data-testid="welcome-login-link-2">
              {isEn ? "Sign in" : "Giriş yapın"}
            </Link>
          </div>
        </div>

        {/* Standards */}
        <div className="mt-12">
          <div className="mb-3 text-center text-[9px] font-medium uppercase tracking-[0.20em] text-[#8a8f83]">
            {t("standardsAligned")}
          </div>
          <div className="mx-auto max-w-lg">
            <StandardsLogos />
          </div>
        </div>

        {/* ---------------------- Features ---------------------- */}
        <section id="features" className="mt-24 scroll-mt-24" data-testid="welcome-features-section">
          <p className="inline-flex items-center gap-2 rounded-full border border-[#14512f]/15 bg-white/70 px-3 py-1 text-[11px] font-medium uppercase tracking-widest text-[#14512f]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#14512f]" />
            {isEn ? "Features" : "Özellikler"}
          </p>
          <h2 className="mt-4 font-display text-3xl leading-tight tracking-tight text-[#16321f] sm:text-4xl">
            {isEn ? "One ledger, every standard." : "Standartları birleştiren tek defter."}
          </h2>
          <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-[#5c6259]">
            {isEn
              ? "ISO 14064, GHG Protocol, CBAM, ESRS, GRI, IFRS S1/S2 and SBTi — all in one auditable record."
              : "ISO 14064, GHG Protokolü, CBAM, ESRS, GRI, IFRS S1/S2 ve SBTi — hepsi tek denetlenebilir kayıtta."}
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={i}
                  data-testid={`welcome-feature-${i}`}
                  className="group rounded-2xl border border-[#e4e6dd] bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-[#14512f]/40 hover:shadow-lg"
                  style={{ boxShadow: "0 4px 14px -8px rgba(20,81,47,0.10)" }}
                >
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#14512f]/10 text-[#14512f]">
                    <Icon className="h-5 w-5" strokeWidth={2} />
                  </span>
                  <h3 className="mt-4 font-display text-lg leading-snug text-[#16321f]">
                    {isEn ? f.title_en : f.title_tr}
                  </h3>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-[#5c6259]">
                    {isEn ? f.text_en : f.text_tr}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ---------------------- Process ---------------------- */}
        <section id="process" className="mt-24 scroll-mt-24" data-testid="welcome-process-section">
          <p className="inline-flex items-center gap-2 rounded-full border border-[#14512f]/15 bg-white/70 px-3 py-1 text-[11px] font-medium uppercase tracking-widest text-[#14512f]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#14512f]" />
            {isEn ? "Process" : "Süreç"}
          </p>
          <h2 className="mt-4 font-display text-3xl leading-tight tracking-tight text-[#16321f] sm:text-4xl">
            {isEn
              ? "An auditable carbon report in three steps."
              : "Üç adımda denetlenebilir karbon raporu."}
          </h2>
          <div className="mt-8 divide-y divide-[#eef0e7] border-t border-[#eef0e7]">
            {process.map((p, i) => (
              <div key={p.no} className="grid grid-cols-1 gap-4 py-8 md:grid-cols-12 md:items-baseline" data-testid={`welcome-process-${i}`}>
                <div className="md:col-span-2">
                  <span className="font-display text-4xl leading-none text-[#14512f]/30 md:text-5xl">
                    {p.no}
                  </span>
                </div>
                <h3 className="font-display text-xl text-[#16321f] md:col-span-3 md:text-2xl">
                  {isEn ? p.title_en : p.title_tr}
                </h3>
                <p className="max-w-xl text-[14.5px] leading-relaxed text-[#5c6259] md:col-span-7">
                  {isEn ? p.text_en : p.text_tr}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ---------------------- Use cases ---------------------- */}
        <section id="use-cases" className="mt-24 scroll-mt-24" data-testid="welcome-usecases-section">
          <p className="inline-flex items-center gap-2 rounded-full border border-[#14512f]/15 bg-white/70 px-3 py-1 text-[11px] font-medium uppercase tracking-widest text-[#14512f]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#14512f]" />
            {isEn ? "Use cases" : "Kullanım"}
          </p>
          <h2 className="mt-4 font-display text-3xl leading-tight tracking-tight text-[#16321f] sm:text-4xl">
            {isEn
              ? "From SMBs to verifiers, for everyone."
              : "KOBİ'den doğrulayıcıya, herkes için."}
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {useCases.map((u, i) => {
              const Icon = u.icon;
              return (
                <div
                  key={i}
                  data-testid={`welcome-usecase-${i}`}
                  className="rounded-2xl border border-[#e4e6dd] bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-[#14512f]/40 hover:shadow-lg"
                  style={{ boxShadow: "0 4px 14px -8px rgba(20,81,47,0.10)" }}
                >
                  <Icon className="h-6 w-6 text-[#14512f]" strokeWidth={1.8} />
                  <h3 className="mt-4 font-display text-lg text-[#16321f]">
                    {isEn ? u.title_en : u.title_tr}
                  </h3>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-[#5c6259]">
                    {isEn ? u.text_en : u.text_tr}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ---------------------- Contact ---------------------- */}
        <section id="contact" className="mt-24 scroll-mt-24" data-testid="welcome-contact-section">
          <p className="inline-flex items-center gap-2 rounded-full border border-[#14512f]/15 bg-white/70 px-3 py-1 text-[11px] font-medium uppercase tracking-widest text-[#14512f]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#14512f]" />
            {isEn ? "Contact" : "İletişim"}
          </p>
          <h2 className="mt-4 font-display text-3xl leading-tight tracking-tight text-[#16321f] sm:text-4xl">
            {isEn ? "Request a demo, start in 30 minutes." : "Demo talep et, 30 dakikada başla."}
          </h2>
          <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-[#5c6259]">
            {isEn
              ? "Our team will show you how Carbon Ledger fits your operations. Reach us via the channels below or request an offer."
              : "Ekibimiz Carbon Ledger'ı işletmenize nasıl uyarlayacağınızı gösterir. Aşağıdaki kanallardan bize ulaşabilir veya teklif talep edebilirsiniz."}
          </p>

          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            <a
              href="mailto:info@enasuspro.com"
              data-testid="welcome-contact-email"
              className="flex items-start gap-3 rounded-2xl border border-[#e4e6dd] bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-[#14512f]/40 hover:shadow-lg"
              style={{ boxShadow: "0 4px 14px -8px rgba(20,81,47,0.10)" }}
            >
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#14512f]/10 text-[#14512f]">
                <Mail className="h-4 w-4" />
              </span>
              <div>
                <div className="text-[11px] font-medium uppercase tracking-widest text-[#8a8f83]">E-posta</div>
                <div className="mt-1 text-[14px] text-[#16321f]">info@enasuspro.com</div>
              </div>
            </a>
            <a
              href="tel:+905323929314"
              data-testid="welcome-contact-phone"
              className="flex items-start gap-3 rounded-2xl border border-[#e4e6dd] bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-[#14512f]/40 hover:shadow-lg"
              style={{ boxShadow: "0 4px 14px -8px rgba(20,81,47,0.10)" }}
            >
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#14512f]/10 text-[#14512f]">
                <Phone className="h-4 w-4" />
              </span>
              <div>
                <div className="text-[11px] font-medium uppercase tracking-widest text-[#8a8f83]">{isEn ? "Phone" : "Telefon"}</div>
                <div className="mt-1 text-[14px] text-[#16321f]">+90 532 392 93 14</div>
              </div>
            </a>
            <div
              data-testid="welcome-contact-address"
              className="flex items-start gap-3 rounded-2xl border border-[#e4e6dd] bg-white p-5"
              style={{ boxShadow: "0 4px 14px -8px rgba(20,81,47,0.10)" }}
            >
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#14512f]/10 text-[#14512f]">
                <MapPin className="h-4 w-4" />
              </span>
              <div>
                <div className="text-[11px] font-medium uppercase tracking-widest text-[#8a8f83]">{isEn ? "Address" : "Adres"}</div>
                <div className="mt-1 text-[13.5px] leading-snug text-[#16321f]">
                  Battal Gazi Sok. No:8/14, 34779 Ataşehir, İstanbul – Türkiye
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/pricing"
              data-testid="welcome-contact-cta-pricing"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#14512f] px-5 py-3 text-[13.5px] font-medium text-white transition-colors hover:bg-[#0f3d24]"
            >
              {isEn ? "Request an offer" : "Teklif Alınız"}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} />
            </Link>
            <Link
              to="/docs"
              data-testid="welcome-contact-cta-docs"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#14512f]/25 bg-white px-5 py-3 text-[13.5px] font-medium text-[#14512f] transition-colors hover:bg-[#f1f4ec]"
            >
              {isEn ? "Read the docs" : "Dokümanları oku"}
            </Link>
          </div>
        </section>

        {/* Footer */}
        <div className="mt-20 border-t border-[#eef0e7] pt-8 text-center text-[12px] text-[#8a8f83]" data-testid="welcome-footer">
          © {new Date().getFullYear()} Enasuspro Carbon Ledger ·{" "}
          <Link to="/landing" className="hover:text-[#14512f] hover:underline" data-testid="welcome-footer-landing-link">
            {isEn ? "Full marketing page" : "Detaylı marketing sayfası"}
          </Link>
        </div>
      </div>
    </div>
  );
}
